import React, { useState, useRef, useEffect } from "react";
import { useFormContext, Controller, useWatch } from "react-hook-form";
import PI, { PhoneInputProps } from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./Form.css";
import { TextCss } from "../../components/GlobalCss/TextCss";
import { useError } from "../../utils/context/ErrorContext";
import { validatePhone } from "../../utils/validateHelper";
import { getPhoneValidationMessage } from "../../utils/phoneValidationMessages";
import { resolvePhoneInputCountry } from "./resolvePhoneInputCountry";

// Workaround for react-phone-input-2 import issues
const ReactPhoneInput: React.FC<PhoneInputProps> = (PI as any).default || PI;

interface CustomPhoneInputProps {
  name: string;
  label?: string;
  required?: boolean;
  requiredMessage?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  defaultCountry?: string;
  /**
   * Restrict dialer countries. Default ["us"].
   * Pass [] to allow all countries (used when WhatsApp messaging is enabled).
   */
  onlyCountries?: string[];
  sectionName?: string;
  inputIndex?: number;
}

const PhoneInput: React.FC<CustomPhoneInputProps> = ({
  label = "Phone Number",
  name,
  required = false,
  requiredMessage = "This field is required",
  disabled = false,
  className = "",
  placeholder = "Enter Contact Number",
  defaultCountry = "us",
  onlyCountries = ["us"],
  sectionName = "",
  inputIndex = undefined,
}) => {
  const {
    control,
    getValues,
    formState: { errors, isSubmitted },
    trigger,
  } = useFormContext();
  const { setErrors, setErrorMessages, errors: contextErrors, errorMessages: contextErrorMessages } = useError();

  const errorKey = sectionName ? `${sectionName}.${name}` : inputIndex !== undefined ? `${name}[${inputIndex}]` : name;
  const contextError = contextErrors[errorKey];
  const contextErrorMessage = contextErrorMessages[errorKey];

  const error = errors[name];
  const allowAllCountries = Array.isArray(onlyCountries) && onlyCountries.length === 0;
  const allowedCountries = allowAllCountries ? null : (onlyCountries?.length ? onlyCountries : ["us"]);
  // Seed from the already-registered RHF value so a key-driven remount
  // (WhatsApp onlyCountries toggle) keeps an international number's country
  // instead of always falling back to defaultCountry ("us").
  const initialCountry = resolvePhoneInputCountry(
    getValues(name) as string | undefined,
    defaultCountry,
    allowedCountries
  );
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  // Mirrors selectedCountry but updates synchronously (unlike the state
  // setter) so the trigger() call below -- which runs in the same tick as
  // the country switch, before React re-renders -- validates against the
  // country the user just picked rather than the previous one.
  const selectedCountryRef = useRef(initialCountry);

  const syncCountryFromValue = (phoneValue: string | undefined) => {
    const nextCountry = resolvePhoneInputCountry(
      phoneValue,
      defaultCountry,
      allowedCountries
    );
    selectedCountryRef.current = nextCountry;
    setSelectedCountry(nextCountry);
  };

  const watchedPhoneValue = useWatch({ control, name });
  useEffect(() => {
    syncCountryFromValue(watchedPhoneValue as string | undefined);
  }, [watchedPhoneValue]);

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className={TextCss.black.sub_heading_600_14}>
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? requiredMessage : false,
          validate: (value) => {
            if (!value && !required) return true;
            const isValid = validatePhone(
              value,
              selectedCountryRef.current,
              () => {},
              () => {},
              sectionName,
              inputIndex
            );
            if (isValid) return true;
            return getPhoneValidationMessage(selectedCountryRef.current, allowAllCountries);
          },
        }}
        render={({ field: { onChange, value, onBlur }, fieldState: { isTouched } }) => {
          // contextError/contextErrorMessage come from validatePhone, which runs on
          // every keystroke (below) — show those live. The RHF-level `error` (e.g.
          // "required" when the field is cleared entirely) only updates on an
          // actual validation run, so it's still gated on touch/submit.
          const showErrorMessage = !!contextError || ((isTouched || isSubmitted) && !!error);

          // Validate on blur explicitly rather than relying on the parent form's
          // `mode` (most forms use the RHF default "onSubmit", which never
          // populates `fieldState.error` until a submit attempt — and Save stays
          // disabled while the form is invalid, so that submit can never happen).
          const handleBlur = () => {
            onBlur();
            trigger(name);
          };

          return (
            <>
              <ReactPhoneInput
                country={selectedCountry}
                value={value}
                onChange={(phone, country) => {
                  const nextCountry = (
                    typeof country === "string"
                      ? country
                      : (country as any)?.countryCode || (country as any)?.iso2 || selectedCountry
                  ).toLowerCase();
                  selectedCountryRef.current = nextCountry;
                  setSelectedCountry(nextCountry);
                  onChange(phone);
                  validatePhone(
                    phone,
                    country,
                    setErrors,
                    setErrorMessages,
                    sectionName,
                    inputIndex
                  );

                  // Must run on every change, not just blur --
                  // react-hook-form's formState.isValid lags a full render
                  // behind field.onChange under mode: "onChange" (a known
                  // RHF quirk), so a Save button gated on isValid stays
                  // disabled for the keystroke that actually made the form
                  // valid, only catching up on the *next* interaction.
                  // Calling trigger() here forces isValid to resolve in the
                  // same interaction.
                  trigger(name);
                }}
                onBlur={handleBlur}
                copyNumbersOnly={true}
                disabled={disabled}
                placeholder={placeholder}
                disableDropdown={allowedCountries != null && allowedCountries.length <= 1}
                {...(allowedCountries ? { onlyCountries: allowedCountries } : {})}
                containerClass="phone-input-container"
                inputClass={`form-control form-control-phone ${showErrorMessage ? "is-invalid" : ""}`}
                inputStyle={{
                  width: "100%",
                }}
                buttonStyle={{
                  border: "none",
                  backgroundColor: "transparent",
                }}
              />
              {showErrorMessage && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {(error?.message as string) || contextErrorMessage}
                </div>
              )}
            </>
          );
        }}
      />
    </div>
  );
};

export default PhoneInput;